<script lang="ts">
  /**
   * The gate in front of the logbook.
   *
   * Three states, and only three: nobody owns this instance yet, somebody does and you are not
   * them, or you are signed in. There is no registration form for a second person, because
   * there is no second person — see the daemon's `auth-routes.ts` for why that is a policy and
   * not an omission.
   *
   * `@simplewebauthn/browser` is here for one reason: `navigator.credentials` speaks
   * ArrayBuffers and the server speaks base64url, and hand-writing that conversion in both
   * directions is how a passkey ends up failing with a `SecurityError` and no explanation.
   */
  import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
  import LoopingPlane from './LoopingPlane.svelte';
  import { api } from './api.ts';
  import type { AuthState } from './api.ts';

  interface Props {
    /**
     * Not called `state`: that name shadows the `$state` rune, and Svelte then parses every
     * `$state(...)` in this file as a store subscription on the prop. The errors it produces
     * talk about `subscribe` and never mention the collision.
     */
    auth: AuthState;
    /** Called after a successful sign-in or claim, so the shell can reload what it shows. */
    onSignedIn: () => void;
  }

  let { auth, onSignedIn }: Props = $props();

  let busy = $state(false);
  let error = $state<string | null>(null);
  let username = $state('');
  let claimToken = $state('');

  /**
   * Whether this browser can do passkeys at all.
   *
   * Checked rather than assumed: over plain HTTP `navigator.credentials` is simply absent, and
   * a button that throws `undefined is not a function` is a worse answer than a sentence
   * explaining that the page has to be served over HTTPS.
   */
  const supported =
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof navigator.credentials?.create === 'function';

  async function signIn(): Promise<void> {
    busy = true;
    error = null;
    try {
      const { flowId, options } = await api.loginOptions();
      const response = await startAuthentication({ optionsJSON: options });
      await api.loginVerify(flowId, response);
      onSignedIn();
    } catch (err) {
      error = message(err);
    } finally {
      busy = false;
    }
  }

  async function claim(): Promise<void> {
    busy = true;
    error = null;
    try {
      const { flowId, options } = await api.registerOptions(username.trim(), claimToken.trim());
      const response = await startRegistration({ optionsJSON: options });
      await api.registerVerify(flowId, response);
      claimToken = '';
      onSignedIn();
    } catch (err) {
      error = message(err);
    } finally {
      busy = false;
    }
  }

  /**
   * The user cancelling is not an error worth showing.
   *
   * Dismissing the system passkey sheet throws `NotAllowedError`, and reporting that as a
   * failure makes a deliberate "not now" look like something broke.
   */
  function message(err: unknown): string | null {
    if (err instanceof Error && err.name === 'NotAllowedError') return null;
    return err instanceof Error ? err.message : String(err);
  }
</script>

<div class="gate">
  <LoopingPlane width={140} seconds={5} />

  {#if !supported}
    <h1>Passkeys brauchen HTTPS</h1>
    <p class="why">
      Dieser Browser stellt <code>navigator.credentials</code> nur in einem sicheren Kontext
      bereit. Über <strong>https://flightlogd.gutbrod.click</strong> funktioniert es; über die
      NodePort-Adresse mit reinem HTTP nicht.
    </p>
  {:else if !auth.claimed}
    <h1>Einrichten</h1>
    {#if !auth.claimPossible}
      <p class="why">
        Die Registrierung ist gesperrt, solange <code>AUTH_TOKEN</code> im Secret nicht gesetzt ist.
        Ohne dieses Token könnte sich der Erstbeste diese Instanz nehmen.
      </p>
    {:else}
      <p class="why">
        Noch niemand besitzt dieses Flugbuch. <strong>Der erste Passkey gewinnt</strong> — danach
        ist die Registrierung dauerhaft zu. Das Token steht im Kubernetes-Secret unter
        <code>AUTH_TOKEN</code>.
      </p>
      <form
        onsubmit={(e) => {
          e.preventDefault();
          void claim();
        }}
      >
        <label>
          <span>Name</span>
          <input bind:value={username} required minlength="2" maxlength="40" autocomplete="username" />
        </label>
        <label>
          <span>Einrichtungs-Token</span>
          <input bind:value={claimToken} required type="password" autocomplete="off" />
        </label>
        <button class="primary" type="submit" disabled={busy}>
          {busy ? 'einen Moment …' : 'Passkey anlegen'}
        </button>
      </form>
    {/if}
  {:else}
    <h1>Anmelden</h1>
    <p class="why">Nichts zu tippen — Passkey wählen und bestätigen.</p>
    <button class="primary" type="button" onclick={() => void signIn()} disabled={busy}>
      {busy ? 'einen Moment …' : 'Mit Passkey anmelden'}
    </button>
  {/if}

  {#if error !== null}
    <p class="bad">{error}</p>
  {/if}
</div>

<style>
  .gate {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.9rem;
    padding: 4rem 1.5rem;
    text-align: center;
  }

  h1 {
    margin: 0;
    font-size: 1.35rem;
    letter-spacing: -0.01em;
  }

  .why {
    margin: 0;
    max-width: 44ch;
    font-size: 0.85rem;
    line-height: 1.55;
    color: var(--text-dim);
  }
  .why code {
    font-size: 0.82em;
    padding: 0.05rem 0.25rem;
    border-radius: 0.25rem;
    background: var(--surface-2, #161a22);
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    width: min(22rem, 100%);
    margin-top: 0.3rem;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    text-align: left;
  }
  label span {
    font-size: 0.72rem;
    color: var(--text-dim);
  }
  input {
    font: inherit;
    font-size: 0.9rem;
    color: var(--text);
    background: var(--bg-raised);
    border: 1px solid var(--line);
    border-radius: 0.5rem;
    padding: 0.5rem 0.65rem;
  }
  input:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 1px;
  }

  .primary {
    font: inherit;
    font-size: 0.9rem;
    font-weight: 600;
    color: #04121a;
    background: var(--accent);
    border: 0;
    border-radius: 999px;
    padding: 0.55rem 1.2rem;
    cursor: pointer;
  }
  .primary:disabled {
    opacity: 0.55;
    cursor: default;
  }

  .bad {
    margin: 0;
    max-width: 46ch;
    font-size: 0.82rem;
    line-height: 1.5;
    color: var(--bad, #e5544b);
  }
</style>
